import React from 'react'
import { css } from '@emotion/css'
import { largeStatsCss, smallStatsCss } from '../text'

export interface CountShorthandProps {
  readonly count: number
  readonly returnPlaneString?: boolean
}

const byteUnits = ['', 'K', 'M', 'G', 'T', 'P', 'E', 'Z', 'Y']

export const ByteShorthand: React.FC<CountShorthandProps> = ({ count }) => (
  <CountShorthand count={count} units={byteUnits} />
)

ByteShorthand.displayName = 'ByteShorthand'

const decimalUnits = ['', 'K', 'M', 'B', 'T', 'Q']

export const DecimalShorthand: React.FC<CountShorthandProps> = ({
  count,
  returnPlaneString
}) =>
  returnPlaneString ? (
    <PlaneCountShorthand count={count} units={decimalUnits} />
  ) : (
    <CountShorthand count={count} units={decimalUnits} />
  )

DecimalShorthand.displayName = 'DecimalShorthand'

export function valueToExponent(value: number, factor: number = 1000): number {
  return value <= 0 ? 0 : Math.floor(Math.log(value) / Math.log(factor))
}

export function normalizeValue(
  value: number,
  factor: number = 1000,
  precision: number = 3
): number {
  return value <= 0
    ? 0
    : parseFloat(
        (value / Math.pow(factor, valueToExponent(value, factor))).toPrecision(
          precision
        )
      )
}

const isValidNumber = (value: number) => !isNaN(value) && isFinite(value)
const isValidCountToDisplay = (count: number, units: string[]) =>
  isValidNumber(count) &&
  Array.isArray(units) &&
  units.length + 1 >= valueToExponent(count)

interface CountShorthandInternalProps {
  readonly count: number
  readonly units: string[]
}

export const CountShorthand: React.FC<CountShorthandInternalProps> = ({
  count,
  units
}) => {
  const valid = isValidCountToDisplay(count, units)
  const unit = valid && units[valueToExponent(count)]

  return (
    <div
      className={css`
        display: flex;
        align-items: baseline;
      `}
    >
      <div className={largeStatsCss}>{valid ? normalizeValue(count) : '-'}</div>
      {unit && (
        <div
          className={css`
            margin-left: 8px;
          `}
        >
          <div className={smallStatsCss}>{unit}</div>
        </div>
      )}
    </div>
  )
}

CountShorthand.displayName = 'CountShorthand'

export const PlaneCountShorthand: React.FC<CountShorthandInternalProps> = ({
  count,
  units
}) => {
  const valid = isValidCountToDisplay(count, units)
  const unit = valid && units[valueToExponent(count)]

  return (
    <span>
      {valid ? normalizeValue(count) : '-'}
      {unit}
    </span>
  )
}

PlaneCountShorthand.displayName = 'PlaneCountShorthand'
